import React, { useState, useRef, useEffect } from 'react';

// Reusable Multi-Select Dropdown Component
// Options format: [{ value: any, label: string }, ...]
const MultiSelectDropdown = ({
  label,
  icon,
  placeholder = 'Select…',
  options = [],
  selectedValues = [],
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-focus search when panel opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [isOpen]);

  const filteredOptions = searchTerm
    ? options.filter((o) => o.label?.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const toggle = (val) =>
    onChange(
      selectedValues.includes(val)
        ? selectedValues.filter((v) => v !== val)
        : [...selectedValues, val]
    );

  const clearAll = (e) => { e.stopPropagation(); onChange([]); setIsOpen(false); };
  const selectAll = () => onChange(filteredOptions.map((o) => o.value));

  const selectedItems = options.filter((o) => selectedValues.includes(o.value));

  return (
    <div className="mb-3">
      {/* Field label */}
      {label && (
        <label className="form-label fw-semibold text-dark">
          {icon && <i className={`${icon} text-primary me-2`} />}
          {label}
        </label>
      )}

      <div ref={dropdownRef} style={{ position: 'relative' }}>
        {/* ── Trigger ── */}
        <div
          onClick={() => setIsOpen((p) => !p)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            border: '1px solid #dee2e6',
            borderRadius: 6,
            background: '#fff',
            cursor: 'pointer',
            minHeight: 38,
            gap: 8,
          }}
        >
          {/* Selected chips / placeholder */}
          <div style={{ flex: 1 }}>
            {selectedItems.length === 0 ? (
              <span style={{ color: '#9ca3af' }}>{placeholder}</span>
            ) : (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {selectedItems.map((item) => (
                  <span
                    key={item.value}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: '#e0f2fe',
                      color: '#0369a1',
                    }}
                  >
                    {item.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Clear + chevron */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {selectedValues.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 13, padding: '0 2px' }}
              >
                ✕
              </button>
            )}
            <span
              style={{
                fontSize: 10,
                color: '#9ca3af',
                display: 'inline-block',
                transition: 'transform 0.15s',
                transform: isOpen ? 'rotate(180deg)' : 'none',
              }}
            >
              ▼
            </span>
          </div>
        </div>

        {/* ── Dropdown panel ── */}
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: '#fff',
              border: '1.5px solid #e2e8f0',
              borderRadius: 8,
              zIndex: 200,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            }}
          >
            {/* Panel header */}
            <div style={{ padding: '6px 12px', borderBottom: '1px solid #f1f5f9' }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {label ? `Select ${label}` : 'Select'}
              </span>
            </div>

            {/* Search */}
            <div style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', background: '#f8f9fa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="fas fa-search" style={{ fontSize: 11, color: '#9ca3af' }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Search…"
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: 12,
                    background: 'transparent',
                    color: '#374151',
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 12, padding: 0 }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Select all */}
            {filteredOptions.length > 0 && (
              <div
                style={{
                  padding: '5px 14px',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type="button"
                  onClick={selectAll}
                  style={{
                    fontSize: 11,
                    color: '#0369a1',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    padding: 0,
                    fontFamily: 'inherit',
                  }}
                >
                  Select all {searchTerm && `(${filteredOptions.length})`}
                </button>
              </div>
            )}

            {/* Options list */}
            <div style={{ overflowY: 'auto', maxHeight: 200 }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  const checked = selectedValues.includes(opt.value);
                  return (
                    <div
                      key={opt.value}
                      onClick={() => toggle(opt.value)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '11px 14px',
                        cursor: 'pointer',
                        background: checked ? '#f0f9ff' : '#fff',
                        borderBottom: '1px solid #f8fafc',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = checked ? '#f0f9ff' : '#fff'; }}
                    >
                      {/* Custom checkbox */}
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: `2px solid ${checked ? '#0369a1' : '#d1d5db'}`,
                          background: checked ? '#0369a1' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.1s',
                        }}
                      >
                        {checked && (
                          <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                            <path d="M1.5 4.5l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: checked ? '#0369a1' : '#374151' }}>
                        {opt.label}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
                  No results found
                </div>
              )}
            </div>

            {/* Footer clear */}
            <div style={{ padding: '8px 14px', borderTop: '1px solid #f1f5f9' }}>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  fontSize: 11,
                  color: '#94a3b8',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                ✕ Clear all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;